import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/workforce.py. Every response carries `meta`, including
// the display timezone (the single system value — render instants with it,
// never with the browser's zone) and the plain-English definition of every
// figure, which the page shows next to the numbers.

export interface WorkforceMeta {
  timezone: string;
  date_from: string; // YYYY-MM-DD, local to `timezone`
  date_to: string;
  shift_tracking_since: string | null; // ISO instant; hours exist only from here
  definitions: Record<string, string>;
}

export type ShiftQuality =
  | "clean"
  | "console_died"
  | "system_signout"
  | "floor"
  | "open"
  | "none";

export interface ShiftSlice {
  shift_id: number;
  start: string;
  end: string;
  ended_by: string | null;
  quality: ShiftQuality;
}

export interface AgentDay {
  day: string;
  agent_key: number;
  agent_name: string;
  talk_s: number;
  wait_s: number;
  disposition_s: number;
  active_s: number;
  pause_s: number;
  on_shift_s: number;
  wait_floored: boolean;
  shift_quality: ShiftQuality;
  shifts: ShiftSlice[];
  calls_connected: number;
  calls_completed: number;
  dispositions: number;
  dispositions_paired: number;
  missed_rings: number;
  calls_outside_shift: number;
  payable_hours: number;
  calls_per_hour: number | null;
}

export interface AgentDaysResponse {
  meta: WorkforceMeta;
  rows: AgentDay[];
}

export interface AbandonRow {
  campaign_id?: number | null;
  campaign_name?: string | null;
  day?: string | null;
  hour?: number | null;
  entered: number;
  connected: number;
  abandoned: number;
  timed_out: number;
  abandon_pct: number | null;
  drop_pct: number | null;
  avg_abandon_wait_s: number | null;
}

export interface AbandonResponse {
  meta: WorkforceMeta;
  by_campaign: AbandonRow[];
  by_hour: AbandonRow[];
}

export interface GapRow {
  agent_key: number;
  agent_name: string;
  gaps: number;
  avg_gap_s: number | null;
  median_gap_s: number | null;
  max_gap_s: number | null;
}

export interface HoldRow {
  campaign_id: number | null;
  campaign_name: string | null;
  calls: number;
  avg_hold_s: number | null;
}

export interface GapsResponse {
  meta: WorkforceMeta;
  per_agent: GapRow[];
  queue_wide: GapRow;
  caller_hold_by_campaign: HoldRow[];
  caller_hold_overall: HoldRow;
}

export interface RangeParams {
  date_from?: string;
  date_to?: string;
}

export async function getAgentDays(params: RangeParams): Promise<AgentDaysResponse> {
  const resp = await api.get<APIResponse<AgentDaysResponse>>("/workforce/agent-days", { params });
  return unwrap(resp.data);
}

export async function getAbandon(
  params: RangeParams & { campaign_id?: number },
): Promise<AbandonResponse> {
  const resp = await api.get<APIResponse<AbandonResponse>>("/workforce/abandon", { params });
  return unwrap(resp.data);
}

export async function getWaitBetweenCalls(params: RangeParams): Promise<GapsResponse> {
  const resp = await api.get<APIResponse<GapsResponse>>("/workforce/wait-between-calls", {
    params,
  });
  return unwrap(resp.data);
}
